import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, Lock, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PageHeader, Card } from "@/components/ranger/PageHeader";
import { CrudTable } from "@/components/ranger/Crud";
import { BTN_PRIMARY } from "@/pages/Servers";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const inputCls = "border-[#1E2D4A] bg-[#050811]";

const Section = ({ cid, path, title, icon, fields, action, actionLabel, active, render, can }) => {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState(Object.fromEntries(fields.map(([k, , d]) => [k, d ?? ""])));
  const load = useCallback(() => api.get(`/communities/${cid}/${path}`).then((r) => setRows(r.data)).catch(() => {}), [cid, path]);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => { e.preventDefault(); try { await api.post(`/communities/${cid}/${path}`, Object.fromEntries(fields.map(([k, , , num]) => [k, num ? Number(f[k]) : f[k]]))); toast.success("Done"); load(); } catch (err) { toast.error(errMsg(err)); } };
  const act = async (id) => { try { await api.post(`/communities/${cid}/${path}/${id}/${action}`); toast.success("Done"); load(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <Card title={title} icon={icon} testId={`${path}-card`} className="mb-5">
      {can && <form onSubmit={submit} className="mb-4 flex flex-wrap gap-2">
        {fields.map(([k, ph, , num]) => <Input key={k} data-testid={`${path}-${k}`} required type={num ? "number" : "text"} placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${inputCls} ${num ? "w-32 font-mono" : "w-48"}`} />)}
        <button data-testid={`${path}-submit`} className={BTN_PRIMARY}>Apply</button>
      </form>}
      {rows.length === 0 ? <p className="text-sm text-slate-500">None yet.</p> : (
        <ul className="divide-y divide-[#1E2D4A]">{rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-3 py-2 font-mono text-xs">
            <span className="text-slate-300">{render(r)}</span>
            <span className="flex items-center gap-3"><span className={r.status === active ? "text-amber-400" : "text-slate-500"}>{r.status}</span>
              {can && r.status === active && <button data-testid={`${path}-${action}-${r.id}`} onClick={() => act(r.id)} className="uppercase text-[#E5202E] hover:text-white">{actionLabel}</button>}</span>
          </li>
        ))}</ul>
      )}
    </Card>
  );
};

export default function Punishments() {
  const { active, can } = useAuth();
  return (
    <div>
      <PageHeader eyebrow="Moderation" title="Temp Bans & Prison" description="Timed punishments that lift themselves. Both use RCON commands that aren't verified on RCE yet; turn them on under Discord Tools after testing on the Console." />
      <Section cid={active.id} path="tempbans" title="Temp bans" icon={Ban} action="lift" actionLabel="Lift" active="active" can={can("admin")}
        fields={[["gamertag", "Gamertag"], ["minutes", "Minutes", 1440, true], ["reason", "Reason"]]}
        render={(b) => `${b.gamertag} · ${b.minutes}m · ${b.reason} · until ${fmtTime(b.until)} · by ${b.by}`} />
      <Section cid={active.id} path="sentences" title="Prison sentences" icon={Lock} action="release" actionLabel="Release" active="jailed" can={can("moderator")}
        fields={[["gamertag", "Gamertag"], ["cell_id", "Cell name"], ["minutes", "Minutes", 30, true], ["reason", "Reason"]]}
        render={(s) => `${s.gamertag} · ${s.cell} · ${s.minutes}m · ${s.reason} · until ${fmtTime(s.until)}`} />
      <CrudTable cid={active.id} path="prison-cells" title="Prison cells" icon={MapPin}
        fields={[{ key: "name", label: "Cell name", default: "" }, { key: "x", label: "X", type: "number", default: 0 }, { key: "y", label: "Y (height)", type: "number", default: 0 }, { key: "z", label: "Z", type: "number", default: 0 }]}
        columns={[["name", "Cell"], ["x", "X"], ["y", "Y"], ["z", "Z"]]} emptyText="Save the map coordinates of each prison cell." />
    </div>
  );
}
